/**
 * Money arithmetic for ROBU.
 *
 * The in-game currency is the *кристалл*. It is nominal: no real money moves
 * inside the platform, and crystals are not convertible here (TZ §2).
 *
 * TZ §17 forbids floating point for money. Every monetary value in this system
 * is a `bigint` count of *minor units* — hundredths of a crystal. All arithmetic
 * below is exact integer arithmetic; conversion to a human-readable string
 * happens only at the presentation boundary.
 */

/** Minor units in one major unit (hundredths per crystal). */
export const MINOR_PER_MAJOR = 100n;

/** Number of fractional digits a major unit has. */
export const MINOR_SCALE = 2;

/** Basis points in 100 % — commission is stored as an integer bps value. */
export const BPS_DENOMINATOR = 10_000n;

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

const AMOUNT_PATTERN = /^-?\d+(?:[.,]\d+)?$/;

/**
 * Parses a human-entered amount ("100", "100.5", "1 234,56") into minor units.
 * Rejects anything with more precision than the currency supports, because
 * silently dropping a hundredth of a crystal is how ledgers stop balancing.
 */
export function parseMinorUnits(input: string | number | bigint): bigint {
  if (typeof input === 'bigint') return input;

  const raw = String(input).replace(/[\s\u00a0_]/g, '');
  if (raw === '' || !AMOUNT_PATTERN.test(raw)) {
    throw new MoneyError(`Invalid monetary amount: "${String(input)}"`);
  }

  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.slice(1) : raw;
  const [wholePart = '0', fractionPart = ''] = unsigned.split(/[.,]/);

  if (fractionPart.length > MINOR_SCALE) {
    throw new MoneyError(
      `Amount "${String(input)}" has more than ${MINOR_SCALE} fractional digits and cannot be represented exactly`,
    );
  }

  const fraction = fractionPart.padEnd(MINOR_SCALE, '0');
  const minor = BigInt(wholePart) * MINOR_PER_MAJOR + BigInt(fraction || '0');
  return negative ? -minor : minor;
}

/** Converts minor units to a plain decimal string ("123456" -> "1234.56"). */
export function minorToDecimalString(minor: bigint): string {
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  const whole = abs / MINOR_PER_MAJOR;
  const fraction = abs % MINOR_PER_MAJOR;
  const fractionStr = fraction.toString().padStart(MINOR_SCALE, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${fractionStr}`;
}

/** Abbreviated currency label for tight layouts such as table cells. */
export const CRYSTAL_SHORT = 'кр.';

/**
 * Picks the Russian declension of «кристалл» for an amount.
 *
 * Two rules apply, and the fractional one comes first: an amount with a
 * non-zero fraction agrees with the fraction, not with the whole part, so 10,50
 * is "10,50 кристалла" and never "10,50 кристаллов".
 */
export function crystalNoun(minor: bigint): string {
  const abs = minor < 0n ? -minor : minor;
  if (abs % MINOR_PER_MAJOR !== 0n) return 'кристалла';

  const whole = abs / MINOR_PER_MAJOR;
  const lastTwo = Number(whole % 100n);
  const last = Number(whole % 10n);

  if (lastTwo >= 11 && lastTwo <= 14) return 'кристаллов';
  if (last === 1) return 'кристалл';
  if (last >= 2 && last <= 4) return 'кристалла';
  return 'кристаллов';
}

export interface FormatMoneyOptions {
  /**
   * Currency label. Defaults to the correctly declined form of «кристалл» for
   * the given amount; pass `CRYSTAL_SHORT` for a compact label or an empty
   * string to omit it entirely.
   */
  currencySymbol?: string;
  /** Thousands separator. Defaults to a narrow no-break space. */
  groupSeparator?: string;
  /** Decimal separator. Defaults to a comma (Russian locale). */
  decimalSeparator?: string;
  /** Prefix non-negative amounts with "+". Useful for ledger rows. */
  alwaysShowSign?: boolean;
}

/** Formats minor units for display: 123456n -> "1 234,56 кристалла". */
export function formatMinor(minor: bigint, options: FormatMoneyOptions = {}): string {
  const {
    currencySymbol = crystalNoun(minor),
    groupSeparator = '\u00a0',
    decimalSeparator = ',',
    alwaysShowSign = false,
  } = options;

  const negative = minor < 0n;
  const [whole = '0', fraction = '00'] = minorToDecimalString(negative ? -minor : minor).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);

  const sign = negative ? '-' : alwaysShowSign ? '+' : '';
  const amount = `${sign}${grouped}${decimalSeparator}${fraction}`;
  return currencySymbol ? `${amount}\u00a0${currencySymbol}` : amount;
}

/**
 * Integer division rounding towards negative infinity.
 *
 * BigInt `/` truncates towards zero, which for negative numerators is *not*
 * a floor. Every rounding decision in this system must be a true floor so that
 * the remainder is always non-negative and can be attributed to the platform.
 */
export function floorDiv(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new MoneyError('Division by zero');
  const quotient = numerator / denominator;
  const hasRemainder = quotient * denominator !== numerator;
  const negativeResult = numerator < 0n !== denominator < 0n;
  return hasRemainder && negativeResult ? quotient - 1n : quotient;
}

/**
 * Applies a basis-point percentage, rounding the result down (TZ §17).
 * Rounding down the commission means the leftover stays in the prize bank,
 * where it is then redistributed or booked as a rounding remainder.
 */
export function applyBasisPoints(amountMinor: bigint, bps: number): bigint {
  if (!Number.isInteger(bps) || bps < 0) {
    throw new MoneyError(`Commission must be a non-negative integer bps value, got ${bps}`);
  }
  return floorDiv(amountMinor * BigInt(bps), BPS_DENOMINATOR);
}

/** Converts a percent value (10, 12.5) into integer basis points (1000, 1250). */
export function percentToBps(percent: string | number): number {
  const minor = parseMinorUnits(percent); // reuse 2-decimal parsing: 12.5 -> 1250
  if (minor < 0n) throw new MoneyError('Commission percent cannot be negative');
  return Number(minor);
}

/** Converts integer basis points back to a percent string ("1000" -> "10.00"). */
export function bpsToPercentString(bps: number): string {
  return minorToDecimalString(BigInt(bps));
}

export function sumMinor(amounts: Iterable<bigint>): bigint {
  let total = 0n;
  for (const amount of amounts) total += amount;
  return total;
}

export function maxMinor(a: bigint, b: bigint): bigint {
  return a > b ? a : b;
}

export function minMinor(a: bigint, b: bigint): bigint {
  return a < b ? a : b;
}

/** JSON-safe representation of money crossing the API boundary. */
export interface MoneyDto {
  /** Exact amount in minor units, serialised as a string to survive JSON. */
  minor: string;
  /** Pre-formatted decimal string for clients that only display the value. */
  decimal: string;
}

export function toMoneyDto(minor: bigint): MoneyDto {
  return { minor: minor.toString(), decimal: minorToDecimalString(minor) };
}

export function fromMoneyDto(dto: MoneyDto | string): bigint {
  return BigInt(typeof dto === 'string' ? dto : dto.minor);
}
